import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import PlayerLayout from "@/components/layout/PlayerLayout";
import AdminLayout from "@/components/layout/AdminLayout";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import DiscordCallback from "@/pages/auth/DiscordCallback";

import Dashboard from "@/pages/player/Dashboard";
import Communities from "@/pages/player/Communities";
import Servers from "@/pages/player/Servers";
import Events from "@/pages/player/Events";
import Connections from "@/pages/player/Connections";
import Notifications from "@/pages/player/Notifications";
import Settings from "@/pages/player/Settings";
import Market from "@/pages/player/Market";
import Clans from "@/pages/player/Clans";
import LFG from "@/pages/player/LFG";
import Maps from "@/pages/player/Maps";
import Leaderboards from "@/pages/player/Leaderboards";
import Messages from "@/pages/player/Messages";
import Support from "@/pages/player/Support";
import PlayerGeneric from "@/pages/player/PlayerGeneric";

import AdminOverview from "@/pages/admin/Overview";
import AdminDiscord from "@/pages/admin/Discord";
import AdminCommunities from "@/pages/admin/Communities";
import AdminServers from "@/pages/admin/Servers";
import AdminPlayers from "@/pages/admin/Players";
import AdminAnnouncements from "@/pages/admin/Announcements";
import AdminEvents from "@/pages/admin/Events";
import AdminRoles from "@/pages/admin/Roles";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminEconomy from "@/pages/admin/Economy";
import { Shop as AdminShop, Kits as AdminKits } from "@/pages/admin/ShopKits";
import AdminAutomation from "@/pages/admin/Automation";
import AdminModeration from "@/pages/admin/Moderation";
import AdminTickets from "@/pages/admin/Tickets";
import AdminMaps from "@/pages/admin/MapsAdmin";
import AdminIntegrations from "@/pages/admin/Integrations";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminSettings from "@/pages/admin/Settings";
import AdminGeneric from "@/pages/admin/AdminGeneric";

function FullLoader() {
    return (
        <div className="min-h-screen hud-grid flex items-center justify-center text-foreground">
            <Loader2 className="animate-spin text-hazard" size={30} />
        </div>
    );
}

function Protected({ children, admin }) {
    const { user, loading, isAdmin } = useAuth();
    if (loading || user === null) return <FullLoader />;
    if (!user) return <Navigate to="/login" replace />;
    if (admin && !isAdmin) return <Navigate to="/player/dashboard" replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, loading, isAdmin } = useAuth();
    if (loading || user === null) return <FullLoader />;
    if (user) return <Navigate to={isAdmin ? "/admin/overview" : "/player/dashboard"} replace />;
    return children;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" theme="dark" richColors />
                    <Routes>
                        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                        <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/auth/discord/callback" element={<Protected><DiscordCallback /></Protected>} />

                        <Route path="/player" element={<Protected><PlayerLayout /></Protected>}>
                            <Route index element={<Navigate to="/player/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="communities" element={<Communities />} />
                            <Route path="servers" element={<Servers />} />
                            <Route path="events" element={<Events />} />
                            <Route path="connections" element={<Connections />} />
                            <Route path="notifications" element={<Notifications />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="market" element={<Market />} />
                            <Route path="clans" element={<Clans />} />
                            <Route path="lfg" element={<LFG />} />
                            <Route path="maps" element={<Maps />} />
                            <Route path="leaderboards" element={<Leaderboards />} />
                            <Route path="messages" element={<Messages />} />
                            <Route path="support" element={<Support />} />
                            <Route path=":section" element={<PlayerGeneric />} />
                        </Route>

                        <Route path="/admin" element={<Protected admin><AdminLayout /></Protected>}>
                            <Route index element={<Navigate to="/admin/overview" replace />} />
                            <Route path="overview" element={<AdminOverview />} />
                            <Route path="discord" element={<AdminDiscord />} />
                            <Route path="communities" element={<AdminCommunities />} />
                            <Route path="servers" element={<AdminServers />} />
                            <Route path="players" element={<AdminPlayers />} />
                            <Route path="announcements" element={<AdminAnnouncements />} />
                            <Route path="events" element={<AdminEvents />} />
                            <Route path="roles" element={<AdminRoles />} />
                            <Route path="economy" element={<AdminEconomy />} />
                            <Route path="shop" element={<AdminShop />} />
                            <Route path="kits" element={<AdminKits />} />
                            <Route path="automation" element={<AdminAutomation />} />
                            <Route path="moderation" element={<AdminModeration />} />
                            <Route path="tickets" element={<AdminTickets />} />
                            <Route path="maps" element={<AdminMaps />} />
                            <Route path="integrations" element={<AdminIntegrations />} />
                            <Route path="analytics" element={<AdminAnalytics />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="audit_logs" element={<AdminAuditLogs />} />
                            <Route path=":section" element={<AdminGeneric />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/player/dashboard" replace />} />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
